/* ============================================================
   Dashboard v3 — Cyberpunk Lime / Breathing Glow + Event Trigger
   ============================================================ */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Candidate } from '../lib/api';

// ─── Inject global keyframes for animations ──────────────
const glowStyles = `
@keyframes breathe {
  0%, 100% { opacity: 0.02; }
  50% { opacity: 0.35; }
}
@keyframes breathe-border {
  0%, 100% { border-color: #000000; box-shadow: inset -8px -8px 15px rgba(0,0,0,0.7), inset 8px 8px 15px rgba(255,255,255,0.05), 0 0 0px rgba(198,249,31,0); }
  50% { border-color: rgba(198,249,31,0.15); box-shadow: inset -8px -8px 15px rgba(0,0,0,0.7), inset 8px 8px 15px rgba(255,255,255,0.05), 0 0 20px rgba(198,249,31,0.08); }
}
.card-frame { animation: breathe-border 8s ease-in-out infinite; }
@keyframes trigger-glow {
  0% { border-color: rgba(198,249,31,0.8); box-shadow: 0 0 30px rgba(198,249,31,0.3), inset -8px -8px 15px rgba(0,0,0,0.7), inset 8px 8px 15px rgba(255,255,255,0.05); }
  100% { border-color: #000000; box-shadow: inset -8px -8px 15px rgba(0,0,0,0.7), inset 8px 8px 15px rgba(255,255,255,0.05); }
}
@keyframes trigger-flash {
  0% { opacity: 0.35; }
  100% { opacity: 0.08; }
}
`;

// ─── Breathing Glow Layer (ditaruh di setiap card) ──────
function Breathe({ type = 'radial' }: { type?: 'radial' | 'streak' }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, borderRadius: 20, overflow: 'hidden',
      pointerEvents: 'none', zIndex: 0,
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: type === 'radial'
          ? 'radial-gradient(circle at 0% 0%, rgba(198,249,31,0.15) 0%, transparent 60%)'
          : 'linear-gradient(135deg, rgba(198,249,31,0.18) 0%, rgba(198,249,31,0.03) 30%, transparent 50%)',
        animation: 'breathe 8s ease-in-out infinite',
      }} />
    </div>
  );
}

// ─── 3D Card — 3 Opsi Gradien ───────────────────────────

// Base: shared shadow + border
const cardBase = {
  borderRadius: 20,
  padding: 24,
  border: '1px solid #000000',
  position: 'relative' as const,
  overflow: 'hidden' as const,
  boxShadow: `
    inset -8px -8px 15px rgba(0, 0, 0, 0.7),
    inset 8px 8px 15px rgba(255, 255, 255, 0.05)
  `,
  transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
};

// Opsi A: Soft Glow Radial
const cardA: React.CSSProperties = {
  ...cardBase,
  background: '#121614',
};

// Opsi B: Light Streak Diagonal
const cardB: React.CSSProperties = {
  ...cardBase,
  background: '#121614',
};

// Opsi C: 3D Bevel
const cardC: React.CSSProperties = {
  ...cardBase,
  background: 'linear-gradient(135deg, #1d2a15 0%, #121614 40%, #0d100e 100%)',
};

const cardSm: React.CSSProperties = {
  ...cardBase,
  borderRadius: 16,
  padding: 18,
  background: '#121614',
};

const C = {
  lime: '#c6f91f',
  limeGlow: '0 0 15px rgba(198,249,31,0.15)',
  bg: '#0b0e0d',
  surface: '#121614',
  well: '#080a09',
  border: '#222824',
  text: '#ffffff',
  muted: '#8e9592',
  green: '#4ade80',
  red: '#f87171',
};

// ─── Icons as SVG components ─────────────────────────────
const I = {
  grid: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  mobile: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>,
  layout: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/></svg>,
  component: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>,
  settings: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  copy: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  arrowUp: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15"/></svg>,
  arrowDown: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>,
};

// ─── Sidebar ─────────────────────────────────────────────
function Sidebar() {
  const nav = [
    { icon: I.grid, label: 'Dashboards', active: true },
    { icon: I.mobile, label: 'Mobile Views', active: false },
    { icon: I.layout, label: 'Landing Pages', active: false },
    { icon: I.component, label: 'UI Components', active: false },
    { icon: I.settings, label: 'Settings', active: false },
  ];

  return (
    <div style={{
      width: 240, background: C.surface, borderRadius: 20, padding: 20,
      display: 'flex', flexDirection: 'column', gap: 8, height: '100%',
      border: '1px solid #000', position: 'relative', overflow: 'hidden',
      boxShadow: `
        inset -8px -8px 15px rgba(0, 0, 0, 0.7),
        inset 8px 8px 15px rgba(255, 255, 255, 0.05)
      `,
    }}>
      {/* 3D gradient overlay (static, no breathing) */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 20,
        background: 'linear-gradient(135deg, rgba(198,249,31,0.04) 0%, transparent 40%)',
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 20px', borderBottom: `1px solid ${C.border}`, marginBottom: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: C.lime, display: 'grid', placeItems: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </div>
        <span style={{ color: C.text, fontWeight: 600, fontSize: 15 }}>TC v3</span>
      </div>

      {nav.map((item, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
          borderRadius: 12, cursor: 'pointer', fontSize: 13,
          background: item.active ? '#1a2218' : 'transparent',
          color: item.active ? C.lime : C.muted,
          border: item.active ? `1px solid ${C.lime}` : '1px solid transparent',
          boxShadow: item.active ? `0 0 12px rgba(198,249,31,0.08)` : 'none',
          transition: 'all 0.2s',
        }}>
          {item.icon}
          <span style={{ fontWeight: item.active ? 600 : 400 }}>{item.label}</span>
        </div>
      ))}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Pro Access */}
      <div style={{
        background: C.well, borderRadius: 14, padding: 16,
        border: '1px solid #000',
        boxShadow: `inset -3px -3px 8px rgba(0,0,0,0.6), inset 3px 3px 8px rgba(255,255,255,0.03)`,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Pro Access</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>7 of 15 projects used</div>
        <div style={{ height: 4, background: '#222824', borderRadius: 4, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ width: '46%', height: '100%', background: C.lime, borderRadius: 4 }} />
        </div>
        <button style={{
          width: '100%', padding: '8px 0', borderRadius: 10, border: 'none',
          background: C.lime, color: '#000', fontWeight: 600, fontSize: 12, cursor: 'pointer',
        }}>Manage Plan</button>
      </div>
      </div>{/* /content wrapper */}
    </div>
  );
}

// ─── Hero Card ───────────────────────────────────────────
function HeroCard({ triggered = false }: { triggered?: boolean }) {
  const extra = triggered ? { borderColor: C.lime, boxShadow: `0 0 30px rgba(198,249,31,0.25), inset -8px -8px 15px rgba(0,0,0,0.7), inset 8px 8px 15px rgba(255,255,255,0.05)` } : {};
  return (
    <div className="card-frame" style={{ ...cardA, ...extra }}>
      <Breathe type="radial" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '3px 10px', borderRadius: 999,
          border: `1px solid ${C.lime}`, color: C.lime,
        }}>Featured</span>
        <span style={{ fontSize: 11, color: C.muted }}>Updated 2h ago</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 8 }}>B2B Analytics OS</div>
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16, maxWidth: 420 }}>
        Enterprise-grade analytics operating system with real-time data aggregation, AI-powered insights, and seamless team collaboration.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['Dashboard OS', 'Complex Layout', 'Analytics'].map(tag => (
          <span key={tag} style={{
            fontSize: 11, padding: '4px 12px', borderRadius: 999,
            background: '#1a2218', color: C.lime, border: `1px solid rgba(198,249,31,0.15)`,
          }}>{tag}</span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{
          padding: '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
          background: C.lime, color: '#000', fontWeight: 700, fontSize: 13,
          boxShadow: C.limeGlow,
        }}>Copy JSON Prompt</button>
        <button style={{
          padding: '10px 24px', borderRadius: 12, border: `1px solid ${C.border}`, cursor: 'pointer',
          background: 'transparent', color: C.muted, fontSize: 13, fontWeight: 500,
        }}>Preview</button>
      </div>
    </div>
  );
}

// ─── Analytics ───────────────────────────────────────────
function Analytics() {
  const metrics = [
    { label: 'Revenue', value: '$128.4K', change: '+12.5%', up: true },
    { label: 'Accounts', value: '1,842', change: '+5.1%', up: true },
    { label: 'MRR', value: '$42.1K', change: '-0.6%', up: false },
    { label: 'Churn', value: '2.3%', change: '-0.4%', up: true },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      {/* Metric Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {metrics.map((m, i) => (
          <div key={i} style={cardSm}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>{m.value}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: m.up ? C.green : C.red }}>
              {m.up ? I.arrowUp : I.arrowDown}
              {m.change}
            </div>
          </div>
        ))}
      </div>

      {/* Mini Donut + Top Accounts */}
      <div style={{ display: 'flex', gap: 12, flex: 1 }}>
        <div style={{ ...cardSm, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="60" height="60" viewBox="0 0 36 36">
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#222824" strokeWidth="3" />
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={C.lime} strokeWidth="3" strokeDasharray="68 100" />
          </svg>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.lime, marginTop: 6 }}>68%</div>
          <div style={{ fontSize: 10, color: C.muted }}>Retention</div>
        </div>
        <div style={{ ...cardSm, flex: 1.5 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Top Accounts</div>
          {['Acme Corp', 'Globex Inc', 'Initech'].map((name, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
              <span style={{ fontSize: 12, color: C.text }}>{name}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.lime }}>${[48, 32, 21][i]}K</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Code Viewer ─────────────────────────────────────────
function CodeViewer({ triggered = false }: { triggered?: boolean }) {
  const extra = triggered ? { borderColor: C.lime, boxShadow: `0 0 30px rgba(198,249,31,0.25), inset -8px -8px 15px rgba(0,0,0,0.7), inset 8px 8px 15px rgba(255,255,255,0.05)` } : {};
  const lines = [
    '{', '  "name": "system_prompt",', '  "version": "2.1.0",', '  "model": "gpt-4-turbo",',
    '  "temperature": 0.7,', '  "max_tokens": 2048,', '  "system": {', '    "role": "analyst",',
    '    "tone": "professional",', '    "context_window": 128000', '  },', '  "agent": {',
    '    "id": "b2b-os-agent-01"', '  }', '}',
  ];

  return (
    <div className="card-frame" style={{ ...cardB, ...extra, padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Breathe type="streak" />
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px', borderBottom: `1px solid ${C.border}`,
        boxShadow: `inset 0 -1px 0 rgba(0,0,0,0.3)`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: C.text }}>system_prompt.json</span>
          <span style={{ color: C.muted, fontSize: 11 }}>/config</span>
        </div>
        <div style={{ color: C.muted, cursor: 'pointer' }}>{I.copy}</div>
      </div>
      {/* Code area */}
      <div style={{ flex: 1, background: C.well, padding: '16px 0', display: 'flex', fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: 12, lineHeight: '22px' }}>
        <div style={{ width: 40, textAlign: 'right', paddingRight: 12, color: '#4a4f4c', userSelect: 'none', borderRight: `1px solid ${C.border}` }}>
          {lines.map((_, i) => <div key={i}>{i + 1}</div>)}
        </div>
        <div style={{ paddingLeft: 16, color: '#c5cdc8', whiteSpace: 'pre' }}>
          {lines.map((line, i) => (
            <div key={i}>
              {line.includes('"name"') || line.includes('"version"') || line.includes('"model"') ? (
                <span><span style={{ color: '#e06c75' }}>{line.split(':')[0]}</span>: <span style={{ color: '#98c379' }}>{line.split(':').slice(1).join(':')}</span></span>
              ) : line.includes('"role"') || line.includes('"tone"') || line.includes('"agent"') ? (
                <span><span style={{ color: '#61afef' }}>{line}</span></span>
              ) : (
                <span>{line}</span>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Footer */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 20px', borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted,
        boxShadow: `inset 0 1px 0 rgba(0,0,0,0.3)`,
      }}>
        <span>JSON • 13 lines</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.green }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} />
          ✓ Valid
        </span>
      </div>
    </div>
  );
}

// ─── Onboarding ──────────────────────────────────────────
function Onboarding() {
  const steps = [
    { num: 'Step 01', label: 'Create workspace', done: true },
    { num: 'Step 02', label: 'Invite members', done: true },
    { num: 'Step 03', label: 'Configure API', done: false, active: true },
  ];

  return (
    <div className="card-frame" style={cardC}>
      <Breathe type="radial" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Onboarding</span>
        <span style={{ fontSize: 11, color: C.muted }}>3 of 5</span>
      </div>
      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 4,
            background: i <= 1 ? C.lime : C.border,
          }} />
        ))}
      </div>
      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px', borderRadius: 12,
            background: s.active ? '#1a2218' : 'transparent',
            border: s.active ? `1px solid rgba(198,249,31,0.15)` : '1px solid transparent',
            transition: 'all 0.2s',
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: 6,
              display: 'grid', placeItems: 'center',
              background: s.done ? C.lime : s.active ? 'rgba(198,249,31,0.1)' : C.border,
              color: s.done ? '#000' : s.active ? C.lime : C.muted,
              fontSize: 11, fontWeight: 700,
            }}>
              {s.done ? I.check : s.num.slice(-2)}
            </div>
            <div style={{ flex: 1, fontSize: 13, color: s.done ? C.muted : C.text, fontWeight: s.active ? 600 : 400 }}>{s.label}</div>
            {s.done && <span style={{ color: C.green, fontSize: 12 }}>✓</span>}
          </div>
        ))}
      </div>
      <button style={{
        width: '100%', padding: '10px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
        background: C.lime, color: '#000', fontWeight: 600, fontSize: 13,
        boxShadow: C.limeGlow,
      }}>Continue</button>
    </div>
  );
}

// ─── Settings ────────────────────────────────────────────
function SettingsPanel() {
  const tabs = ['General', 'Security', 'Billing'];
  const toggles = [
    { label: 'Email notifications', on: true },
    { label: 'Weekly digest', on: false },
    { label: 'Dark mode sync', on: true },
    { label: 'Auto-save drafts', on: true },
  ];

  return (
    <div className="card-frame" style={cardA}>
      <Breathe type="radial" />
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {tabs.map((tab, i) => (
          <button key={i} style={{
            padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: i === 0 ? 600 : 400,
            background: i === 0 ? C.lime : C.border,
            color: i === 0 ? '#000' : C.muted,
            transition: 'all 0.2s',
          }}>{tab}</button>
        ))}
      </div>

      {/* Toggles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {toggles.map((t, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 4px', borderRadius: 8, cursor: 'pointer',
            transition: 'background 0.2s',
          }}>
            <span style={{ fontSize: 13, color: C.text }}>{t.label}</span>
            <div style={{
              width: 36, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer',
              background: t.on ? C.lime : C.border,
              transition: 'background 0.2s',
            }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute',
                top: 2, left: t.on ? 18 : 2, transition: 'left 0.2s',
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Smart Defaults */}
      <div style={{
        marginTop: 16, padding: 14, borderRadius: 14,
        background: C.well, border: `1px solid ${C.lime}`,
        boxShadow: `0 0 15px rgba(198,249,31,0.1), inset -3px -3px 8px rgba(0,0,0,0.5), inset 3px 3px 8px rgba(255,255,255,0.02)`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Smart defaults</span>
          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: C.lime, color: '#000', fontWeight: 600, textTransform: 'uppercase' }}>Pro</span>
        </div>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          AI-powered configuration recommendations based on your usage patterns.
        </div>
      </div>
    </div>
  );
}

// ─── Main Layout ─────────────────────────────────────────
export function DashboardV3Page() {
  const [triggered, setTriggered] = useState(false);
  const { data: candidatesData } = useQuery({
    queryKey: ['candidates'],
    queryFn: () => api.get<{ candidates: Candidate[] }>('/api/bot/candidates?limit=10'),
    refetchInterval: 30_000,
  });
  const qc = useQueryClient();
  const runBot = useMutation({
    mutationFn: () => api.post<any>('/api/bot/run', { narrate: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      setTriggered(true);
      setTimeout(() => setTriggered(false), 2000);
    },
  });

  return (
    <div style={{
      background: C.bg, minHeight: '100%', padding: 20,
      position: 'relative', overflow: 'auto',
    }}>
      {/* Inject keyframes */}
      <style>{glowStyles}</style>

      {/* Ambient glow */}
      <div style={{
        position: 'fixed', top: '-20%', left: '50%', transform: 'translateX(-50%)',
        width: '80%', height: '60%',
        background: 'radial-gradient(ellipse at center, rgba(198,249,31,0.03) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      {/* Run Bot overlay indicator */}
      {triggered && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 100,
          padding: '8px 16px', borderRadius: 10,
          background: C.lime, color: '#000', fontWeight: 600, fontSize: 12,
          display: 'flex', alignItems: 'center', gap: 6,
          animation: 'trigger-glow 2s ease-out',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#000' }} />
          Bot Triggered
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 32, height: '100%' }}>
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content Grid */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          {/* Top Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.5fr 1.2fr', gap: 20, minHeight: 0 }}>
            <HeroCard triggered={triggered} />
            <Analytics />
            <CodeViewer triggered={triggered} />
          </div>

          {/* Bottom Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Onboarding />
            <SettingsPanel />
          </div>

          {/* Run Bot button */}
          <button
            onClick={() => runBot.mutate()}
            disabled={runBot.isPending}
            style={{
              padding: '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: C.lime, color: '#000', fontWeight: 700, fontSize: 13,
              boxShadow: C.limeGlow, alignSelf: 'flex-start',
              transition: 'all 0.3s',
              opacity: runBot.isPending ? 0.7 : 1,
            }}
          >
            {runBot.isPending ? '⏳ Running...' : '⚡ Trigger Bot'}
          </button>
        </div>
      </div>
    </div>
  );
}
